import type { ShowNotificationOptions } from "@toolpad/core";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import i18n from "@/config/i18n";
import type { GraphQLError } from "@/utils/graphqlError";

type ForbiddenApiToastOptions = Pick<ShowNotificationOptions, "severity">;

type ForbiddenApiToastNotifications = {
  show: (message: string, options: ForbiddenApiToastOptions) => void;
};

type ForbiddenReadableErrorCode =
  | "FORBIDDEN"
  | "ACCESS_DENIED"
  | "AUTH_NOT_AUTHORIZED"
  | "PERMISSION_FAILURE"
  | "RESOURCE_NOT_BELONGS"
  | "ACCOUNT_UNAVAILABLE"
  | "EMPTY_PERMISSIONS"
  | "INVALID_ROLE_PERMISSIONS"
  | "INVALID_IMPERSONATED_ROLE_ERROR";

type ForbiddenTranslationKey =
  | "access_denied.action_forbidden_toast"
  | "access_denied.invalid_role_permissions_toast"
  | "access_denied.invalid_impersonated_role_error";

const FORBIDDEN_GRAPHQL_CODES = new Set<ForbiddenReadableErrorCode>([
  "FORBIDDEN",
  "ACCESS_DENIED",
  "AUTH_NOT_AUTHORIZED",
  "PERMISSION_FAILURE",
  "RESOURCE_NOT_BELONGS",
  "ACCOUNT_UNAVAILABLE",
  "EMPTY_PERMISSIONS",
  "INVALID_ROLE_PERMISSIONS",
  "INVALID_IMPERSONATED_ROLE_ERROR",
]);

const ERROR_TRANSLATION_KEYS: Partial<
  Record<ForbiddenReadableErrorCode, ForbiddenTranslationKey>
> = {
  INVALID_ROLE_PERMISSIONS: "access_denied.invalid_role_permissions_toast",
  INVALID_IMPERSONATED_ROLE_ERROR:
    "access_denied.invalid_impersonated_role_error",
};

const DEFAULT_FORBIDDEN_TRANSLATION_KEY: ForbiddenTranslationKey =
  "access_denied.action_forbidden_toast";

let forbiddenApiToastNotifier: ForbiddenApiToastNotifications | null = null;

function isForbiddenReadableErrorCode(
  value: unknown
): value is ForbiddenReadableErrorCode {
  return (
    typeof value === "string" &&
    FORBIDDEN_GRAPHQL_CODES.has(value as ForbiddenReadableErrorCode)
  );
}

function getMatchedForbiddenCode(
  errors: readonly GraphQLError[] | undefined
): ForbiddenReadableErrorCode | null {
  const firstError = errors?.[0];
  if (!firstError) {
    return null;
  }

  const readableErrorCode = firstError.extensions?.readableErrorCode;

  if (isForbiddenReadableErrorCode(readableErrorCode)) {
    return readableErrorCode;
  }

  return null;
}

function graphQlErrorsArrayIndicatesForbidden(
  errors: readonly GraphQLError[] | undefined
): boolean {
  return getMatchedForbiddenCode(errors) !== null;
}

export function isForbiddenApiError(error: unknown): boolean {
  if (CombinedGraphQLErrors.is(error)) {
    return graphQlErrorsArrayIndicatesForbidden(error.errors);
  }
  return false;
}

export function registerForbiddenApiToastNotifier(
  notifier: ForbiddenApiToastNotifications
): void {
  forbiddenApiToastNotifier = notifier;
}

export function unregisterForbiddenApiToastNotifier(): void {
  forbiddenApiToastNotifier = null;
}

export function showForbiddenApiToastIfApplicable(error: unknown): boolean {
  if (!CombinedGraphQLErrors.is(error)) {
    return false;
  }

  const matchedCode = getMatchedForbiddenCode(error.errors);
  if (!matchedCode) {
    return false;
  }

  const translationKey =
    ERROR_TRANSLATION_KEYS[matchedCode] ?? DEFAULT_FORBIDDEN_TRANSLATION_KEY;

  const message = i18n.t(translationKey);

  if (!forbiddenApiToastNotifier) {
    return false;
  }

  forbiddenApiToastNotifier.show(message, {
    severity: "error",
  });
  return true;
}
